import React, { useState, useCallback, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axiosInstance from "../../axios/axiosInstance";
import ConfirmModal from "components/ConfirmModal";

const companyId = () => localStorage.getItem("companyId") || "platform";

const api = {
    getYears:    ()           => axiosInstance.get(`/companies/${companyId()}/tax-brackets/years`).then(r => r.data),
    getBrackets: (year, jur)  => axiosInstance.get(`/companies/${companyId()}/tax-brackets?year=${year}&jurisdiction=${jur}`).then(r => r.data),
    update:      (id, body)   => axiosInstance.put(`/companies/${companyId()}/tax-brackets/${id}`, body).then(r => r.data),
    create:      (body)       => axiosInstance.post(`/companies/${companyId()}/tax-brackets`, body).then(r => r.data),
    remove:      (id)         => axiosInstance.delete(`/companies/${companyId()}/tax-brackets/${id}`),
    copyYear:    (body)       => axiosInstance.post(`/companies/${companyId()}/tax-brackets/copy-year`, body).then(r => r.data),
};

const yearApi = {
    status: (year) => axiosInstance.get(`/admin/tax-year/status`, { params: year ? { year } : {} }).then(r => r.data),
    copy:   (body) => axiosInstance.post(`/admin/tax-year/copy`, body).then(r => r.data),
    importBrackets: (body) => axiosInstance.post(`/admin/tax-year/import-brackets`, body).then(r => r.data),
    applyDefaults: (body) => axiosInstance.post(`/admin/tax-year/apply-published-defaults`, body).then(r => r.data),
    downloadCsv: async (path, filename, params = {}) => {
        const r = await axiosInstance.get(path, { params, responseType: "blob" });
        const url = URL.createObjectURL(r.data);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
    },
};

const sdiApi = {
    getAll:  (year)     => axiosInstance.get(`/admin/tax-flat-rates?taxType=SDI&year=${year}`).then(r => r.data),
    update:  (id, body) => axiosInstance.put(`/admin/tax-flat-rates/${id}`, body).then(r => r.data),
    create:  (body)     => axiosInstance.post(`/admin/tax-flat-rates`, body).then(r => r.data),
    remove:  (id)       => axiosInstance.delete(`/admin/tax-flat-rates/${id}`),
};

const localApi = {
    getAll:  (state, year) => axiosInstance.get(`/admin/local-tax-rates?state=${state}&year=${year}`).then(r => r.data),
    update:  (id, body)    => axiosInstance.put(`/admin/local-tax-rates/${id}`, body).then(r => r.data),
    create:  (body)        => axiosInstance.post(`/admin/local-tax-rates`, body).then(r => r.data),
    remove:  (id)          => axiosInstance.delete(`/admin/local-tax-rates/${id}`),
};

const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC"];

const fmtRate = v => v != null ? (Number(v) * 100).toFixed(4) + "%" : "—";
const fmtAmt  = v => v != null ? Number(v).toLocaleString("en-US", { style: "currency", currency: "USD" }) : "—";

const inp = { fontSize: 12, border: "1px solid #d1d5db", borderRadius: 4, padding: "3px 6px" };
const sel = { ...inp, padding: "3px 24px 3px 6px", appearance: "auto", WebkitAppearance: "auto", minWidth: 70 };
const btn = { fontSize: 12, border: "none", borderRadius: 4, padding: "4px 10px", cursor: "pointer" };

const EditRow = ({ bracket, onSave, onCancel }) => {
    const [form, setForm] = useState({ ...bracket });
    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
    return (
        <tr style={{ background: "#fffbeb" }}>
            <td style={{ padding: "4px 8px", fontFamily: "monospace", fontSize: 11 }}>{bracket.id}</td>
            <td><select value={form.filingStatus} onChange={e => set("filingStatus", e.target.value)} style={inp}><option>S</option><option>MFJ</option><option>MFS</option><option>HOH</option><option>ALL</option></select></td>
            <td><input value={form.minWage ?? ""} onChange={e => set("minWage", e.target.value)} style={{ ...inp, width: 90 }} /></td>
            <td><input value={form.maxWage ?? ""} onChange={e => set("maxWage", e.target.value || null)} style={{ ...inp, width: 90 }} placeholder="∞" /></td>
            <td><input value={form.baseAmount ?? ""} onChange={e => set("baseAmount", e.target.value)} style={{ ...inp, width: 90 }} /></td>
            <td><input value={form.rate ?? ""} onChange={e => set("rate", e.target.value)} style={{ ...inp, width: 90 }} placeholder="0.05 = 5%" /></td>
            <td><input value={form.excessOver ?? ""} onChange={e => set("excessOver", e.target.value)} style={{ ...inp, width: 90 }} /></td>
            <td>
                <button onClick={() => onSave(form)} style={{ ...btn, background: "#16a34a", color: "#fff", marginRight: 4 }}>Save</button>
                <button onClick={onCancel} style={{ ...btn, background: "#6b7280", color: "#fff" }}>Cancel</button>
            </td>
        </tr>
    );
};

const BracketTable = ({ jurisdiction, taxType, yearOverride, onYearChange }) => {
    const qc = useQueryClient();
    const [year, setYear]       = useState(null);
    const [copyFrom, setCopyFrom] = useState(null);
    const [copyTo, setCopyTo]   = useState(new Date().getFullYear());
    const [copyMsg, setCopyMsg] = useState("");
    const [copyOverwrite, setCopyOverwrite] = useState(false);
    const [confirmCopy, setConfirmCopy] = useState(false);
    const [editId, setEditId]   = useState(null);
    const [addRow, setAddRow]   = useState(false);
    const [newRow, setNewRow]   = useState({ year: null, jurisdiction, taxType, filingStatus: "S", minWage: 0, maxWage: null, baseAmount: 0, rate: 0, excessOver: 0 });
    const [confirmDeleteId, setConfirmDeleteId] = useState(null);

    const { data: years = [] } = useQuery({ queryKey: ["tb-years"], queryFn: api.getYears });

    useEffect(() => {
        if (years.length > 0) {
            if (!year) setYear(yearOverride ?? years[0]);
            if (!copyFrom) setCopyFrom(yearOverride ?? years[0]);
        }
    }, [years]); // eslint-disable-line react-hooks/exhaustive-deps

    const selectYear = (y) => {
        setYear(y);
        if (onYearChange) onYearChange(y);
    };

    const { data: brackets = [], isLoading } = useQuery({
        queryKey: ["tb", year, jurisdiction],
        queryFn: () => api.getBrackets(year, jurisdiction),
        enabled: !!year,
    });

    const invalidate = useCallback(() => {
        qc.invalidateQueries({ queryKey: ["tb", year, jurisdiction] });
        qc.invalidateQueries({ queryKey: ["tb-years"] });
        qc.invalidateQueries({ queryKey: ["tax-year-status"] });
    }, [qc, year, jurisdiction]);

    const updateMut = useMutation({ mutationFn: ({ id, body }) => api.update(id, body), onSuccess: () => { setEditId(null); invalidate(); } });
    const createMut = useMutation({ mutationFn: api.create, onSuccess: () => { setAddRow(false); invalidate(); } });
    const deleteMut = useMutation({ mutationFn: api.remove, onSuccess: invalidate });
    const copyMut   = useMutation({
        mutationFn: () => api.copyYear({
            fromYear: Number(copyFrom),
            toYear: Number(copyTo),
            overwrite: copyOverwrite,
            jurisdiction,
            taxType,
        }),
        onSuccess: r => {
            setCopyMsg(`Copied ${r.copied} ${jurisdiction}/${taxType} brackets from ${copyFrom} → ${copyTo}.`);
            setConfirmCopy(false);
            setCopyOverwrite(false);
            selectYear(Number(copyTo));
            invalidate();
        },
        onError: e => {
            const msg = e?.response?.data?.message || e?.response?.data || "Copy failed.";
            setCopyMsg(typeof msg === "string" ? msg : "Copy failed — year may already exist. Enable overwrite and retry.");
            setConfirmCopy(false);
        },
    });

    const handleSave   = (id, form) => updateMut.mutate({ id, body: { ...form, rate: Number(form.rate), minWage: Number(form.minWage), maxWage: form.maxWage != null ? Number(form.maxWage) : null, baseAmount: Number(form.baseAmount), excessOver: Number(form.excessOver) } });
    const handleCreate = () => createMut.mutate({ ...newRow, year: Number(year), rate: Number(newRow.rate), minWage: Number(newRow.minWage), maxWage: newRow.maxWage != null ? Number(newRow.maxWage) : null, baseAmount: Number(newRow.baseAmount), excessOver: Number(newRow.excessOver) });

    return (
        <div>
            {/* Year Status Bar */}
            {year && (() => {
                const currentYear = new Date().getFullYear();
                const maxYear = years.length > 0 ? Math.max(...years) : null;
                const isLatest = year === maxYear;
                const isCurrent = year === currentYear;
                const missingPayYear = maxYear != null && !years.includes(currentYear);
                return (
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
                        <div style={{
                            display: "flex", alignItems: "center", gap: 8,
                            background: "#1a3a5c", color: "#fff",
                            borderRadius: 8, padding: "10px 18px", fontSize: 13,
                        }}>
                            <span style={{ fontSize: 20 }}>📋</span>
                            <span>Viewing <strong>{year}</strong> tax brackets</span>
                            {isLatest && isCurrent && <span style={{ background: "#16a34a", color: "#fff", borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 700 }}>✓ Current Year</span>}
                            {isLatest && !isCurrent && <span style={{ background: "#1B75F0", color: "#fff", borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 700 }}>Latest Available</span>}
                            {!isLatest && <span style={{ background: "#6b7280", color: "#fff", borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 700 }}>Archive</span>}
                        </div>
                        <div style={{ fontSize: 12, color: "#475569", maxWidth: 520, lineHeight: 1.45 }}>
                            Payroll uses the <strong>pay-date year</strong> first; if that year has no rows, it falls back to <strong>{maxYear ?? "—"}</strong> (latest in DB).
                            {missingPayYear && <span style={{ display: "block", marginTop: 4, color: "#dc2626", fontWeight: 600 }}>⚠️ {currentYear} brackets missing — add/copy them before running {currentYear} payroll.</span>}
                        </div>
                        <div style={{ marginLeft: "auto", fontSize: 11, color: "#94a3b8" }}>
                            Available years: {years.join(", ") || "—"}
                        </div>
                    </div>
                );
            })()}

            {/* Copy Year Panel */}
            <div style={{ background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 8, padding: 16, marginBottom: 24 }}>
                <p style={{ fontWeight: 700, fontSize: 13, marginBottom: 4, color: "#0369a1" }}>Copy brackets for {jurisdiction} / {taxType}</p>
                <p style={{ fontSize: 11, color: "#0369a1", margin: "0 0 10px", opacity: 0.85 }}>
                    Copies only this jurisdiction/type. For a full-year pack (all states + settings + SDI), use the Year Rollover checklist above.
                </p>
                <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                    <div>
                        <label style={{ fontSize: 12, fontWeight: 600, marginRight: 6 }}>From Year:</label>
                        <select value={copyFrom ?? ""} onChange={e => setCopyFrom(Number(e.target.value))} style={{ ...inp, padding: "5px 8px", minWidth: 80 }}>
                            {years.length === 0 && <option value="">Loading…</option>}
                            {years.map(y => <option key={y} value={y}>{y}</option>)}
                        </select>
                    </div>
                    <span style={{ fontSize: 18, color: "#94a3b8" }}>→</span>
                    <div>
                        <label style={{ fontSize: 12, fontWeight: 600, marginRight: 6 }}>To Year:</label>
                        <input type="number" value={copyTo} onChange={e => setCopyTo(e.target.value)} style={{ ...inp, width: 80, padding: "5px 8px" }} />
                    </div>
                    <label style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                        <input type="checkbox" checked={copyOverwrite} onChange={e => setCopyOverwrite(e.target.checked)} />
                        Overwrite if target exists
                    </label>
                    <button onClick={() => setConfirmCopy(true)} disabled={!copyFrom || copyMut.isPending}
                        style={{ ...btn, background: "#0369a1", color: "#fff", padding: "6px 16px" }}>
                        {copyMut.isPending ? "Copying…" : "Copy Year"}
                    </button>
                    {copyMsg && <span style={{ fontSize: 12, color: String(copyMsg).includes("Copied") ? "#16a34a" : "#dc2626" }}>{String(copyMsg)}</span>}
                </div>
            </div>

            {/* Filter Bar */}
            <div style={{ display: "flex", gap: 16, alignItems: "center", marginBottom: 16, flexWrap: "wrap" }}>
                <div>
                    <label style={{ fontSize: 12, fontWeight: 600, marginRight: 6 }}>Year:</label>
                    <select value={year ?? ""} onChange={e => selectYear(Number(e.target.value))} style={{ ...inp, padding: "6px 10px", minWidth: 80 }}>
                        {years.length === 0 && <option value="">Loading…</option>}
                        {years.map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                </div>
                <button onClick={() => { setAddRow(true); setNewRow({ year, jurisdiction, taxType, filingStatus: "S", minWage: 0, maxWage: null, baseAmount: 0, rate: 0, excessOver: 0 }); }}
                    style={{ ...btn, background: "#1B75F0", color: "#fff", padding: "6px 14px", marginLeft: "auto" }}>
                    + Add Bracket
                </button>
            </div>

            {isLoading && <p style={{ color: "#888" }}>Loading…</p>}

            {!isLoading && (
                <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                        <thead>
                            <tr style={{ background: "#1a3a5c", color: "#fff" }}>
                                {["ID", "Filing Status", "Min Wage", "Max Wage", "Base Amount", "Rate", "Excess Over", "Actions"].map(h => (
                                    <th key={h} style={{ padding: "10px 10px", textAlign: "left", fontWeight: 600, whiteSpace: "nowrap" }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {addRow && (
                                <tr style={{ background: "#f0fdf4" }}>
                                    <td style={{ padding: "4px 8px", color: "#aaa", fontSize: 11 }}>new</td>
                                    <td><select value={newRow.filingStatus} onChange={e => setNewRow(r => ({ ...r, filingStatus: e.target.value }))} style={inp}><option>S</option><option>MFJ</option><option>MFS</option><option>HOH</option><option>ALL</option></select></td>
                                    <td><input value={newRow.minWage} onChange={e => setNewRow(r => ({ ...r, minWage: e.target.value }))} style={{ ...inp, width: 90 }} /></td>
                                    <td><input value={newRow.maxWage ?? ""} onChange={e => setNewRow(r => ({ ...r, maxWage: e.target.value || null }))} style={{ ...inp, width: 90 }} placeholder="∞" /></td>
                                    <td><input value={newRow.baseAmount} onChange={e => setNewRow(r => ({ ...r, baseAmount: e.target.value }))} style={{ ...inp, width: 90 }} /></td>
                                    <td><input value={newRow.rate} onChange={e => setNewRow(r => ({ ...r, rate: e.target.value }))} style={{ ...inp, width: 90 }} placeholder="0.05" /></td>
                                    <td><input value={newRow.excessOver} onChange={e => setNewRow(r => ({ ...r, excessOver: e.target.value }))} style={{ ...inp, width: 90 }} /></td>
                                    <td>
                                        <button onClick={handleCreate} style={{ ...btn, background: "#16a34a", color: "#fff", marginRight: 4 }}>Add</button>
                                        <button onClick={() => setAddRow(false)} style={{ ...btn, background: "#6b7280", color: "#fff" }}>Cancel</button>
                                    </td>
                                </tr>
                            )}
                            {brackets.length === 0 && !addRow && (
                                <tr><td colSpan={8} style={{ padding: 20, textAlign: "center", color: "#aaa" }}>No brackets found for {jurisdiction} / {year}.</td></tr>
                            )}
                            {brackets.map((b, i) =>
                                editId === b.id ? (
                                    <EditRow key={b.id} bracket={b} onSave={form => handleSave(b.id, form)} onCancel={() => setEditId(null)} />
                                ) : (
                                    <tr key={b.id} style={{ background: i % 2 === 0 ? "#fff" : "#f9fafb", borderBottom: "1px solid #e5e7eb" }}>
                                        <td style={{ padding: "8px 10px", fontFamily: "monospace", fontSize: 11, color: "#9ca3af" }}>{b.id}</td>
                                        <td style={{ padding: "8px 10px", fontWeight: 600 }}>{b.filingStatus}</td>
                                        <td style={{ padding: "8px 10px" }}>{fmtAmt(b.minWage)}</td>
                                        <td style={{ padding: "8px 10px" }}>{b.maxWage != null ? fmtAmt(b.maxWage) : <span style={{ color: "#aaa" }}>No limit</span>}</td>
                                        <td style={{ padding: "8px 10px" }}>{fmtAmt(b.baseAmount)}</td>
                                        <td style={{ padding: "8px 10px", fontWeight: 700, color: "#1a3a5c" }}>{fmtRate(b.rate)}</td>
                                        <td style={{ padding: "8px 10px" }}>{fmtAmt(b.excessOver)}</td>
                                        <td style={{ padding: "8px 10px", whiteSpace: "nowrap" }}>
                                            <button onClick={() => setEditId(b.id)} style={{ ...btn, background: "#e0f2fe", color: "#0369a1", marginRight: 4 }}>Edit</button>
                                            <button onClick={() => setConfirmDeleteId(b.id)} style={{ ...btn, background: "#fee2e2", color: "#dc2626" }}>Del</button>
                                        </td>
                                    </tr>
                                )
                            )}
                        </tbody>
                    </table>
                    {brackets.length > 0 && <p style={{ fontSize: 11, color: "#aaa", marginTop: 6 }}>{brackets.length} bracket(s) for {jurisdiction} / {year}</p>}
                </div>
            )}

            <ConfirmModal
                show={!!confirmDeleteId}
                title="Delete Tax Bracket?"
                message="This bracket will be permanently removed and cannot be recovered."
                confirmText="Delete"
                onConfirm={() => { deleteMut.mutate(confirmDeleteId); setConfirmDeleteId(null); }}
                onCancel={() => setConfirmDeleteId(null)}
            />
            <ConfirmModal
                show={confirmCopy}
                title={copyOverwrite ? "Overwrite & copy year?" : "Copy brackets to new year?"}
                message={copyOverwrite
                    ? `This will DELETE existing ${jurisdiction}/${taxType} brackets for ${copyTo}, then copy from ${copyFrom}.`
                    : `Copy ${jurisdiction}/${taxType} brackets from ${copyFrom} → ${copyTo}. If ${copyTo} already has rows, the copy will fail unless overwrite is checked.`}
                confirmText={copyOverwrite ? "Overwrite & Copy" : "Copy"}
                onConfirm={() => copyMut.mutate()}
                onCancel={() => setConfirmCopy(false)}
            />
        </div>
    );
};

const StateBracketTable = () => {
    const [state, setState] = useState("CA");
    const qc = useQueryClient();
    return (
        <div>
            <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 20 }}>
                <label style={{ fontSize: 12, fontWeight: 600 }}>State:</label>
                <select value={state} onChange={e => { setState(e.target.value); qc.invalidateQueries({ queryKey: ["tb"] }); }}
                    style={{ ...inp, padding: "6px 10px", minWidth: 80 }}>
                    {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <span style={{ fontSize: 12, color: "#6b7280" }}>Select a state to view and edit its income tax brackets.</span>
            </div>
            <BracketTable key={state} jurisdiction={state} taxType="SIT" />
        </div>
    );
};

const reciprocityApi = {
    getAll:  ()          => axiosInstance.get(`/admin/reciprocity`).then(r => r.data),
    create:  (body)      => axiosInstance.post(`/admin/reciprocity`, body).then(r => r.data),
    update:  (id, body)  => axiosInstance.put(`/admin/reciprocity/${id}`, body).then(r => r.data),
    remove:  (id)        => axiosInstance.delete(`/admin/reciprocity/${id}`),
};

const settingsApi = {
    getAll:  (key, year) => axiosInstance.get(`/admin/tax-settings?settingKey=${key}&year=${year}`).then(r => r.data),
    update:  (id, body)  => axiosInstance.put(`/admin/tax-settings/${id}`, body).then(r => r.data),
    create:  (body)      => axiosInstance.post(`/admin/tax-settings`, body).then(r => r.data),
    remove:  (id)        => axiosInstance.delete(`/admin/tax-settings/${id}`),
};

const SETTING_KEYS = [
    { key: "SS_WAGE_BASE",    label: "Social Security Wage Base", hasState: false, format: "dollar", hint: "Update each November when IRS publishes. Applies to all employees." },
    { key: "FUTA_RATE",       label: "FUTA Net Rate",             hasState: true,  format: "rate",   hint: "Standard is 0.006 (0.6%). Add state-specific rows for credit reduction states (DOL publishes in November)." },
    { key: "SUTA_WAGE_BASE",  label: "SUTA Wage Base per State",  hasState: true,  format: "dollar", hint: "Use 'DEFAULT' for the fallback. Per-company rate is set in Company Settings." },
    { key: "STATE_ALLOWANCE", label: "State W-4 Allowance Amount",hasState: true,  format: "dollar", hint: "Annual allowance amount per exemption claimed on state W-4. Use 'DEFAULT' as fallback." },
];

const TaxSettingsTable = ({ settingKey, label, hasState, format, hint, year }) => {
    const [editId, setEditId] = useState(null);
    const [editForm, setEditForm] = useState({});
    const [addRow, setAddRow] = useState(false);
    const [newRow, setNewRow] = useState({ settingKey, state: hasState ? "" : null, year, value: 0, notes: "" });
    const [confirmDeleteId, setConfirmDeleteId] = useState(null);

    const { data: rows = [], isLoading, refetch } = useQuery({
        queryKey: ["tax-settings", settingKey, year],
        queryFn: () => settingsApi.getAll(settingKey, year),
    });

    const updateMut = useMutation({ mutationFn: ({ id, body }) => settingsApi.update(id, body), onSuccess: () => { setEditId(null); refetch(); } });
    const createMut = useMutation({ mutationFn: settingsApi.create, onSuccess: () => { setAddRow(false); refetch(); } });
    const deleteMut = useMutation({ mutationFn: settingsApi.remove, onSuccess: refetch });

    const fmtVal = v => format === "rate" ? (Number(v) * 100).toFixed(4) + "%" : "$" + Number(v).toLocaleString();

    const cols = hasState ? ["State", "Value", "Notes", "Actions"] : ["Value", "Notes", "Actions"];

    return (
        <div style={{ marginBottom: 32 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                <div>
                    <h4 style={{ fontSize: 13, fontWeight: 700, margin: 0, color: "#1a3a5c" }}>{label}</h4>
                    <p style={{ fontSize: 11, color: "#6b7280", margin: "2px 0 0" }}>{hint}</p>
                </div>
                <button onClick={() => { setAddRow(true); setNewRow({ settingKey, state: hasState ? "" : null, year, value: 0, notes: "" }); }}
                    style={{ ...btn, background: "#1B75F0", color: "#fff", padding: "5px 12px", whiteSpace: "nowrap" }}>
                    + Add Row
                </button>
            </div>
            {isLoading && <p style={{ fontSize: 12, color: "#aaa" }}>Loading…</p>}
            <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                    <thead>
                        <tr style={{ background: "#1a3a5c", color: "#fff" }}>
                            {cols.map(h => <th key={h} style={{ padding: "8px 12px", textAlign: "left", fontWeight: 600 }}>{h}</th>)}
                        </tr>
                    </thead>
                    <tbody>
                        {addRow && (
                            <tr style={{ background: "#f0fdf4" }}>
                                {hasState && <td style={{ padding: "4px 8px" }}><input value={newRow.state ?? ""} onChange={e => setNewRow(r => ({ ...r, state: e.target.value.toUpperCase() }))} style={{ ...inp, width: 80 }} placeholder="CA or DEFAULT" /></td>}
                                <td style={{ padding: "4px 8px" }}><input value={newRow.value} onChange={e => setNewRow(r => ({ ...r, value: e.target.value }))} style={{ ...inp, width: 110 }} placeholder={format === "rate" ? "0.006" : "176100"} /></td>
                                <td style={{ padding: "4px 8px" }}><input value={newRow.notes} onChange={e => setNewRow(r => ({ ...r, notes: e.target.value }))} style={{ ...inp, width: 180 }} placeholder="Optional note" /></td>
                                <td style={{ padding: "4px 8px" }}>
                                    <button onClick={() => createMut.mutate({ ...newRow, value: Number(newRow.value) })} style={{ ...btn, background: "#16a34a", color: "#fff", marginRight: 4 }}>Add</button>
                                    <button onClick={() => setAddRow(false)} style={{ ...btn, background: "#6b7280", color: "#fff" }}>Cancel</button>
                                </td>
                            </tr>
                        )}
                        {rows.length === 0 && !addRow && (
                            <tr><td colSpan={cols.length} style={{ padding: 16, textAlign: "center", color: "#aaa" }}>No rows for {year}.</td></tr>
                        )}
                        {rows.map((r, i) => editId === r.id ? (
                            <tr key={r.id} style={{ background: "#fffbeb" }}>
                                {hasState && <td style={{ padding: "4px 8px" }}><input value={editForm.state ?? ""} onChange={e => setEditForm(f => ({ ...f, state: e.target.value.toUpperCase() }))} style={{ ...inp, width: 80 }} /></td>}
                                <td style={{ padding: "4px 8px" }}><input value={editForm.value ?? ""} onChange={e => setEditForm(f => ({ ...f, value: e.target.value }))} style={{ ...inp, width: 110 }} /></td>
                                <td style={{ padding: "4px 8px" }}><input value={editForm.notes ?? ""} onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))} style={{ ...inp, width: 180 }} /></td>
                                <td style={{ padding: "4px 8px" }}>
                                    <button onClick={() => updateMut.mutate({ id: r.id, body: { ...editForm, value: Number(editForm.value) } })} style={{ ...btn, background: "#16a34a", color: "#fff", marginRight: 4 }}>Save</button>
                                    <button onClick={() => setEditId(null)} style={{ ...btn, background: "#6b7280", color: "#fff" }}>Cancel</button>
                                </td>
                            </tr>
                        ) : (
                            <tr key={r.id} style={{ background: i % 2 === 0 ? "#fff" : "#f9fafb", borderBottom: "1px solid #e5e7eb" }}>
                                {hasState && <td style={{ padding: "8px 12px", fontWeight: 600, fontFamily: "monospace" }}>{r.state || <span style={{ color: "#aaa" }}>federal</span>}</td>}
                                <td style={{ padding: "8px 12px", fontWeight: 700, color: "#1a3a5c" }}>{fmtVal(r.value)}</td>
                                <td style={{ padding: "8px 12px", color: "#6b7280", fontSize: 11 }}>{r.notes || "—"}</td>
                                <td style={{ padding: "8px 12px", whiteSpace: "nowrap" }}>
                                    <button onClick={() => { setEditId(r.id); setEditForm({ ...r }); }} style={{ ...btn, background: "#e0f2fe", color: "#0369a1", marginRight: 4 }}>Edit</button>
                                    <button onClick={() => setConfirmDeleteId(r.id)} style={{ ...btn, background: "#fee2e2", color: "#dc2626" }}>Del</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <ConfirmModal show={!!confirmDeleteId} title="Delete Tax Setting?" message="This setting will be removed. Payroll will fall back to hardcoded defaults."
                confirmText="Delete" onConfirm={() => { deleteMut.mutate(confirmDeleteId); setConfirmDeleteId(null); }} onCancel={() => setConfirmDeleteId(null)} />
        </div>
    );
};

const SDI_STATES = ["CA","CO","CT","HI","MA","NJ","NY","OR","RI","WA"];
const LOCAL_STATES = ["IN","KY","MD","MI","MO","NY","OH","PA"];

const SdiRatesTable = () => {
    const qc = useQueryClient();
    const [year, setYear] = useState(new Date().getFullYear());
    const [editId, setEditId] = useState(null);
    const [addRow, setAddRow] = useState(false);
    const [newRow, setNewRow] = useState({ jurisdiction: "CA", taxType: "SDI", rate: 0, wageBase: null, year });
    const [editForm, setEditForm] = useState({});
    const [confirmDeleteId, setConfirmDeleteId] = useState(null);

    const { data: rows = [], isLoading, refetch } = useQuery({
        queryKey: ["sdi-rates", year],
        queryFn: () => sdiApi.getAll(year),
    });

    const updateMut = useMutation({ mutationFn: ({ id, body }) => sdiApi.update(id, body), onSuccess: () => { setEditId(null); refetch(); } });
    const createMut = useMutation({ mutationFn: sdiApi.create, onSuccess: () => { setAddRow(false); refetch(); } });
    const deleteMut = useMutation({ mutationFn: sdiApi.remove, onSuccess: refetch });

    const years = [2024, 2025, 2026, 2027, 2028];

    return (
        <div>
            <div style={{ display: "flex", gap: 16, alignItems: "center", marginBottom: 16, flexWrap: "wrap" }}>
                <div>
                    <label style={{ fontSize: 12, fontWeight: 600, marginRight: 6 }}>Year:</label>
                    <select value={year} onChange={e => setYear(Number(e.target.value))} style={{ ...sel, padding: "6px 28px 6px 10px" }}>
                        {years.map(y => <option key={y}>{y}</option>)}
                    </select>
                </div>
                <button onClick={() => { setAddRow(true); setNewRow({ jurisdiction: "CA", taxType: "SDI", rate: 0, wageBase: null, year }); }}
                    style={{ ...btn, background: "#1B75F0", color: "#fff", padding: "6px 14px", marginLeft: "auto" }}>
                    + Add SDI Rate
                </button>
            </div>
            {isLoading && <p style={{ color: "#888" }}>Loading…</p>}
            <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                    <thead>
                        <tr style={{ background: "#1a3a5c", color: "#fff" }}>
                            {["State", "Rate", "Wage Base", "Year", "Actions"].map(h => (
                                <th key={h} style={{ padding: "10px 12px", textAlign: "left", fontWeight: 600 }}>{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {addRow && (
                            <tr style={{ background: "#f0fdf4" }}>
                                <td style={{ padding: "4px 8px" }}>
                                    <select value={newRow.jurisdiction} onChange={e => setNewRow(r => ({ ...r, jurisdiction: e.target.value }))} style={sel}>
                                        {SDI_STATES.map(s => <option key={s}>{s}</option>)}
                                    </select>
                                </td>
                                <td><input value={newRow.rate} onChange={e => setNewRow(r => ({ ...r, rate: e.target.value }))} style={{ ...inp, width: 90 }} placeholder="0.011" /></td>
                                <td><input value={newRow.wageBase ?? ""} onChange={e => setNewRow(r => ({ ...r, wageBase: e.target.value || null }))} style={{ ...inp, width: 110 }} placeholder="No cap" /></td>
                                <td><select value={newRow.year} onChange={e => setNewRow(r => ({ ...r, year: Number(e.target.value) }))} style={sel}>{years.map(y => <option key={y}>{y}</option>)}</select></td>
                                <td>
                                    <button onClick={() => createMut.mutate({ ...newRow, rate: Number(newRow.rate), wageBase: newRow.wageBase ? Number(newRow.wageBase) : null })}
                                        style={{ ...btn, background: "#16a34a", color: "#fff", marginRight: 4 }}>Add</button>
                                    <button onClick={() => setAddRow(false)} style={{ ...btn, background: "#6b7280", color: "#fff" }}>Cancel</button>
                                </td>
                            </tr>
                        )}
                        {rows.length === 0 && !addRow && (
                            <tr><td colSpan={5} style={{ padding: 20, textAlign: "center", color: "#aaa" }}>No SDI rates found for {year}.</td></tr>
                        )}
                        {rows.map((r, i) => editId === r.id ? (
                            <tr key={r.id} style={{ background: "#fffbeb" }}>
                                <td style={{ padding: "4px 8px" }}>
                                    <select value={editForm.jurisdiction} onChange={e => setEditForm(f => ({ ...f, jurisdiction: e.target.value }))} style={sel}>
                                        {SDI_STATES.map(s => <option key={s}>{s}</option>)}
                                    </select>
                                </td>
                                <td><input value={editForm.rate ?? ""} onChange={e => setEditForm(f => ({ ...f, rate: e.target.value }))} style={{ ...inp, width: 90 }} /></td>
                                <td><input value={editForm.wageBase ?? ""} onChange={e => setEditForm(f => ({ ...f, wageBase: e.target.value || null }))} style={{ ...inp, width: 110 }} placeholder="No cap" /></td>
                                <td><select value={editForm.year ?? year} onChange={e => setEditForm(f => ({ ...f, year: Number(e.target.value) }))} style={sel}>{years.map(y => <option key={y}>{y}</option>)}</select></td>
                                <td>
                                    <button onClick={() => updateMut.mutate({ id: r.id, body: { ...editForm, rate: Number(editForm.rate), wageBase: editForm.wageBase ? Number(editForm.wageBase) : null } })}
                                        style={{ ...btn, background: "#16a34a", color: "#fff", marginRight: 4 }}>Save</button>
                                    <button onClick={() => setEditId(null)} style={{ ...btn, background: "#6b7280", color: "#fff" }}>Cancel</button>
                                </td>
                            </tr>
                        ) : (
                            <tr key={r.id} style={{ background: i % 2 === 0 ? "#fff" : "#f9fafb", borderBottom: "1px solid #e5e7eb" }}>
                                <td style={{ padding: "8px 12px", fontWeight: 700 }}>{r.jurisdiction}</td>
                                <td style={{ padding: "8px 12px", fontWeight: 700, color: "#1a3a5c" }}>{fmtRate(r.rate)}</td>
                                <td style={{ padding: "8px 12px" }}>{r.wageBase ? fmtAmt(r.wageBase) : <span style={{ color: "#aaa" }}>No cap</span>}</td>
                                <td style={{ padding: "8px 12px" }}>{r.year}</td>
                                <td style={{ padding: "8px 12px", whiteSpace: "nowrap" }}>
                                    <button onClick={() => { setEditId(r.id); setEditForm({ ...r }); }} style={{ ...btn, background: "#e0f2fe", color: "#0369a1", marginRight: 4 }}>Edit</button>
                                    <button onClick={() => setConfirmDeleteId(r.id)} style={{ ...btn, background: "#fee2e2", color: "#dc2626" }}>Del</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <ConfirmModal show={!!confirmDeleteId} title="Delete SDI Rate?" message="This SDI rate will be permanently removed."
                confirmText="Delete" onConfirm={() => { deleteMut.mutate(confirmDeleteId); setConfirmDeleteId(null); }} onCancel={() => setConfirmDeleteId(null)} />
        </div>
    );
};

const LocalTaxRatesTable = () => {
    const [state, setLocalState] = useState("MD");
    const [year, setYear] = useState(new Date().getFullYear());
    const [editId, setEditId] = useState(null);
    const [editForm, setEditForm] = useState({});
    const [addRow, setAddRow] = useState(false);
    const [newRow, setNewRow] = useState({ state, localityCode: "", localityName: "", rate: 0, year });
    const [confirmDeleteId, setConfirmDeleteId] = useState(null);

    const { data: rows = [], isLoading, refetch } = useQuery({
        queryKey: ["local-rates", state, year],
        queryFn: () => localApi.getAll(state, year),
    });

    const updateMut = useMutation({ mutationFn: ({ id, body }) => localApi.update(id, body), onSuccess: () => { setEditId(null); refetch(); } });
    const createMut = useMutation({ mutationFn: localApi.create, onSuccess: () => { setAddRow(false); refetch(); } });
    const deleteMut = useMutation({ mutationFn: localApi.remove, onSuccess: refetch });

    const years = [2024, 2025, 2026, 2027, 2028];

    return (
        <div>
            <div style={{ display: "flex", gap: 16, alignItems: "center", marginBottom: 16, flexWrap: "wrap" }}>
                <div>
                    <label style={{ fontSize: 12, fontWeight: 600, marginRight: 6 }}>State:</label>
                    <select value={state} onChange={e => { setLocalState(e.target.value); setEditId(null); setAddRow(false); }} style={{ ...sel, padding: "6px 28px 6px 10px" }}>
                        {LOCAL_STATES.map(s => <option key={s}>{s}</option>)}
                    </select>
                </div>
                <div>
                    <label style={{ fontSize: 12, fontWeight: 600, marginRight: 6 }}>Year:</label>
                    <select value={year} onChange={e => setYear(Number(e.target.value))} style={{ ...sel, padding: "6px 28px 6px 10px" }}>
                        {years.map(y => <option key={y}>{y}</option>)}
                    </select>
                </div>
                <button onClick={() => { setAddRow(true); setNewRow({ state, localityCode: "", localityName: "", rate: 0, year }); }}
                    style={{ ...btn, background: "#1B75F0", color: "#fff", padding: "6px 14px", marginLeft: "auto" }}>
                    + Add Locality
                </button>
            </div>
            {isLoading && <p style={{ color: "#888" }}>Loading…</p>}
            <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                    <thead>
                        <tr style={{ background: "#1a3a5c", color: "#fff" }}>
                            {["Locality Code", "Locality Name", "Rate", "Actions"].map(h => (
                                <th key={h} style={{ padding: "10px 12px", textAlign: "left", fontWeight: 600 }}>{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {addRow && (
                            <tr style={{ background: "#f0fdf4" }}>
                                <td style={{ padding: "4px 8px" }}><input value={newRow.localityCode} onChange={e => setNewRow(r => ({ ...r, localityCode: e.target.value.toUpperCase() }))} style={{ ...inp, width: 130 }} placeholder="MONTGOMERY" /></td>
                                <td><input value={newRow.localityName} onChange={e => setNewRow(r => ({ ...r, localityName: e.target.value }))} style={{ ...inp, width: 180 }} placeholder="Montgomery County" /></td>
                                <td><input value={newRow.rate} onChange={e => setNewRow(r => ({ ...r, rate: e.target.value }))} style={{ ...inp, width: 90 }} placeholder="0.032" /></td>
                                <td>
                                    <button onClick={() => createMut.mutate({ ...newRow, rate: Number(newRow.rate) })}
                                        style={{ ...btn, background: "#16a34a", color: "#fff", marginRight: 4 }}>Add</button>
                                    <button onClick={() => setAddRow(false)} style={{ ...btn, background: "#6b7280", color: "#fff" }}>Cancel</button>
                                </td>
                            </tr>
                        )}
                        {rows.length === 0 && !addRow && (
                            <tr><td colSpan={4} style={{ padding: 20, textAlign: "center", color: "#aaa" }}>No local tax rates found for {state} / {year}.</td></tr>
                        )}
                        {rows.map((r, i) => editId === r.id ? (
                            <tr key={r.id} style={{ background: "#fffbeb" }}>
                                <td style={{ padding: "4px 8px" }}><input value={editForm.localityCode ?? ""} onChange={e => setEditForm(f => ({ ...f, localityCode: e.target.value.toUpperCase() }))} style={{ ...inp, width: 130 }} /></td>
                                <td><input value={editForm.localityName ?? ""} onChange={e => setEditForm(f => ({ ...f, localityName: e.target.value }))} style={{ ...inp, width: 180 }} /></td>
                                <td><input value={editForm.rate ?? ""} onChange={e => setEditForm(f => ({ ...f, rate: e.target.value }))} style={{ ...inp, width: 90 }} /></td>
                                <td>
                                    <button onClick={() => updateMut.mutate({ id: r.id, body: { ...editForm, rate: Number(editForm.rate) } })}
                                        style={{ ...btn, background: "#16a34a", color: "#fff", marginRight: 4 }}>Save</button>
                                    <button onClick={() => setEditId(null)} style={{ ...btn, background: "#6b7280", color: "#fff" }}>Cancel</button>
                                </td>
                            </tr>
                        ) : (
                            <tr key={r.id} style={{ background: i % 2 === 0 ? "#fff" : "#f9fafb", borderBottom: "1px solid #e5e7eb" }}>
                                <td style={{ padding: "8px 12px", fontFamily: "monospace", fontWeight: 600 }}>{r.localityCode}</td>
                                <td style={{ padding: "8px 12px" }}>{r.localityName}</td>
                                <td style={{ padding: "8px 12px", fontWeight: 700, color: "#1a3a5c" }}>{fmtRate(r.rate)}</td>
                                <td style={{ padding: "8px 12px", whiteSpace: "nowrap" }}>
                                    <button onClick={() => { setEditId(r.id); setEditForm({ ...r }); }} style={{ ...btn, background: "#e0f2fe", color: "#0369a1", marginRight: 4 }}>Edit</button>
                                    <button onClick={() => setConfirmDeleteId(r.id)} style={{ ...btn, background: "#fee2e2", color: "#dc2626" }}>Del</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {rows.length > 0 && <p style={{ fontSize: 11, color: "#aaa", marginTop: 6 }}>{rows.length} locality rate(s) for {state} / {year}</p>}
            </div>
            <ConfirmModal show={!!confirmDeleteId} title="Delete Local Tax Rate?" message="This locality rate will be permanently removed."
                confirmText="Delete" onConfirm={() => { deleteMut.mutate(confirmDeleteId); setConfirmDeleteId(null); }} onCancel={() => setConfirmDeleteId(null)} />
        </div>
    );
};

const ReciprocityTable = () => {
    const [editId, setEditId] = useState(null);
    const [editForm, setEditForm] = useState({});
    const [addRow, setAddRow] = useState(false);
    const [newRow, setNewRow] = useState({ residentState: "", workState: "", isActive: true, notes: "" });
    const [confirmDeleteId, setConfirmDeleteId] = useState(null);
    const [search, setSearch] = useState("");

    const { data: rows = [], isLoading, refetch } = useQuery({
        queryKey: ["reciprocity"],
        queryFn: reciprocityApi.getAll,
    });

    const updateMut = useMutation({ mutationFn: ({ id, body }) => reciprocityApi.update(id, body), onSuccess: () => { setEditId(null); refetch(); } });
    const createMut = useMutation({ mutationFn: reciprocityApi.create, onSuccess: () => { setAddRow(false); setNewRow({ residentState: "", workState: "", isActive: true, notes: "" }); refetch(); } });
    const deleteMut = useMutation({ mutationFn: reciprocityApi.remove, onSuccess: refetch });

    const filtered = rows.filter(r =>
        !search || r.residentState?.includes(search.toUpperCase()) || r.workState?.includes(search.toUpperCase())
    );

    return (
        <div>
            <div style={{ display: "flex", gap: 16, alignItems: "center", marginBottom: 16, flexWrap: "wrap" }}>
                <div>
                    <label style={{ fontSize: 12, fontWeight: 600, marginRight: 6 }}>Filter by State:</label>
                    <input value={search} onChange={e => setSearch(e.target.value)} placeholder="e.g. PA"
                        style={{ ...inp, width: 80, padding: "5px 8px" }} />
                </div>
                <button onClick={() => setAddRow(true)}
                    style={{ ...btn, background: "#1B75F0", color: "#fff", padding: "6px 14px", marginLeft: "auto" }}>
                    + Add Pair
                </button>
            </div>
            {isLoading && <p style={{ color: "#888" }}>Loading…</p>}
            <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                    <thead>
                        <tr style={{ background: "#1a3a5c", color: "#fff" }}>
                            {["Resident State", "Work State", "Active", "Notes", "Actions"].map(h => (
                                <th key={h} style={{ padding: "10px 12px", textAlign: "left", fontWeight: 600 }}>{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {addRow && (
                            <tr style={{ background: "#f0fdf4" }}>
                                <td style={{ padding: "4px 8px" }}>
                                    <select value={newRow.residentState} onChange={e => setNewRow(r => ({ ...r, residentState: e.target.value }))} style={sel}>
                                        <option value="">— Pick —</option>
                                        {US_STATES.map(s => <option key={s}>{s}</option>)}
                                    </select>
                                </td>
                                <td style={{ padding: "4px 8px" }}>
                                    <select value={newRow.workState} onChange={e => setNewRow(r => ({ ...r, workState: e.target.value }))} style={sel}>
                                        <option value="">— Pick —</option>
                                        {US_STATES.map(s => <option key={s}>{s}</option>)}
                                    </select>
                                </td>
                                <td style={{ padding: "4px 8px" }}>
                                    <input type="checkbox" checked={newRow.isActive} onChange={e => setNewRow(r => ({ ...r, isActive: e.target.checked }))} />
                                </td>
                                <td style={{ padding: "4px 8px" }}>
                                    <input value={newRow.notes} onChange={e => setNewRow(r => ({ ...r, notes: e.target.value }))} style={{ ...inp, width: 200 }} placeholder="Optional note" />
                                </td>
                                <td style={{ padding: "4px 8px" }}>
                                    <button onClick={() => createMut.mutate(newRow)} style={{ ...btn, background: "#16a34a", color: "#fff", marginRight: 4 }}>Add</button>
                                    <button onClick={() => setAddRow(false)} style={{ ...btn, background: "#6b7280", color: "#fff" }}>Cancel</button>
                                </td>
                            </tr>
                        )}
                        {filtered.length === 0 && !addRow && (
                            <tr><td colSpan={5} style={{ padding: 20, textAlign: "center", color: "#aaa" }}>No reciprocity pairs found.</td></tr>
                        )}
                        {filtered.map((r, i) => editId === r.id ? (
                            <tr key={r.id} style={{ background: "#fffbeb" }}>
                                <td style={{ padding: "4px 8px" }}>
                                    <select value={editForm.residentState} onChange={e => setEditForm(f => ({ ...f, residentState: e.target.value }))} style={sel}>
                                        {US_STATES.map(s => <option key={s}>{s}</option>)}
                                    </select>
                                </td>
                                <td style={{ padding: "4px 8px" }}>
                                    <select value={editForm.workState} onChange={e => setEditForm(f => ({ ...f, workState: e.target.value }))} style={sel}>
                                        {US_STATES.map(s => <option key={s}>{s}</option>)}
                                    </select>
                                </td>
                                <td style={{ padding: "4px 8px" }}>
                                    <input type="checkbox" checked={editForm.isActive} onChange={e => setEditForm(f => ({ ...f, isActive: e.target.checked }))} />
                                </td>
                                <td style={{ padding: "4px 8px" }}>
                                    <input value={editForm.notes ?? ""} onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))} style={{ ...inp, width: 200 }} />
                                </td>
                                <td style={{ padding: "4px 8px" }}>
                                    <button onClick={() => updateMut.mutate({ id: r.id, body: editForm })} style={{ ...btn, background: "#16a34a", color: "#fff", marginRight: 4 }}>Save</button>
                                    <button onClick={() => setEditId(null)} style={{ ...btn, background: "#6b7280", color: "#fff" }}>Cancel</button>
                                </td>
                            </tr>
                        ) : (
                            <tr key={r.id} style={{ background: i % 2 === 0 ? "#fff" : "#f9fafb", borderBottom: "1px solid #e5e7eb" }}>
                                <td style={{ padding: "8px 12px", fontWeight: 700, fontFamily: "monospace" }}>{r.residentState}</td>
                                <td style={{ padding: "8px 12px", fontWeight: 700, fontFamily: "monospace" }}>{r.workState}</td>
                                <td style={{ padding: "8px 12px" }}>
                                    <span style={{ background: r.isActive ? "#dcfce7" : "#fee2e2", color: r.isActive ? "#16a34a" : "#dc2626", borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 700 }}>
                                        {r.isActive ? "Active" : "Inactive"}
                                    </span>
                                </td>
                                <td style={{ padding: "8px 12px", color: "#6b7280", fontSize: 11 }}>{r.notes || "—"}</td>
                                <td style={{ padding: "8px 12px", whiteSpace: "nowrap" }}>
                                    <button onClick={() => { setEditId(r.id); setEditForm({ ...r }); }} style={{ ...btn, background: "#e0f2fe", color: "#0369a1", marginRight: 4 }}>Edit</button>
                                    <button onClick={() => setConfirmDeleteId(r.id)} style={{ ...btn, background: "#fee2e2", color: "#dc2626" }}>Del</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {filtered.length > 0 && <p style={{ fontSize: 11, color: "#aaa", marginTop: 6 }}>{filtered.length} reciprocity pair(s){search ? ` matching "${search.toUpperCase()}"` : ""}</p>}
            </div>
            <ConfirmModal show={!!confirmDeleteId} title="Delete Reciprocity Pair?"
                message="This pair will be removed. If both directions exist, delete both to fully remove the agreement."
                confirmText="Delete" onConfirm={() => { deleteMut.mutate(confirmDeleteId); setConfirmDeleteId(null); }} onCancel={() => setConfirmDeleteId(null)} />
        </div>
    );
};

const EasyAnnualUpdate = () => {
    const qc = useQueryClient();
    const calendarYear = new Date().getFullYear();
    const [toYear, setToYear] = useState(calendarYear);
    const [fromYear, setFromYear] = useState(calendarYear - 1);
    const [stepMsg, setStepMsg] = useState("");
    const [confirmCopy, setConfirmCopy] = useState(false);
    const [confirmDefaults, setConfirmDefaults] = useState(false);

    const { data: status } = useQuery({
        queryKey: ["tax-year-status", toYear],
        queryFn: () => yearApi.status(toYear),
    });

    const invalidateAll = () => {
        qc.invalidateQueries({ queryKey: ["tax-year-status"] });
        qc.invalidateQueries({ queryKey: ["tb-years"] });
        qc.invalidateQueries({ queryKey: ["tb"] });
        qc.invalidateQueries({ queryKey: ["tax-settings"] });
        qc.invalidateQueries({ queryKey: ["sdi-rates"] });
    };

    const copyMut = useMutation({
        mutationFn: () => yearApi.copy({
            fromYear: Number(fromYear),
            toYear: Number(toYear),
            overwrite: true,
            copyBrackets: true,
            copySettings: true,
            copyFlatRates: true,
            copyLocalRates: true,
        }),
        onSuccess: (r) => {
            const parts = Object.entries(r.copied || {}).map(([k, v]) => `${k}: ${v}`).join(", ");
            setStepMsg(`Step 1 done — copied ${fromYear} → ${toYear} (${parts}). Continue to Step 2.`);
            setConfirmCopy(false);
            invalidateAll();
        },
        onError: (e) => {
            setStepMsg(e?.response?.data?.message || "Copy failed.");
            setConfirmCopy(false);
        },
    });

    const defaultsMut = useMutation({
        mutationFn: () => yearApi.applyDefaults({
            year: Number(toYear),
            overwrite: true,
            applySsWageBase: true,
            applySdi: true,
        }),
        onSuccess: (r) => {
            const notes = (r.notes || []).join(" ");
            setStepMsg(`${r.message || "Defaults applied."} ${notes} Then update FITW in Step 3 (Federal tab).`);
            setConfirmDefaults(false);
            invalidateAll();
        },
        onError: (e) => {
            setStepMsg(e?.response?.data?.message || "Apply defaults failed.");
            setConfirmDefaults(false);
        },
    });

    const stepBox = (n, title, body, action) => (
        <div style={{
            border: "1px solid #e2e8f0", borderRadius: 10, padding: 14,
            background: "#fff", display: "flex", flexDirection: "column", gap: 10, minHeight: 140,
        }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{
                    width: 24, height: 24, borderRadius: "50%", background: "#1B75F0", color: "#fff",
                    fontSize: 12, fontWeight: 800, display: "inline-flex", alignItems: "center", justifyContent: "center",
                }}>{n}</span>
                <span style={{ fontSize: 13, fontWeight: 800, color: "#1a3a5c" }}>{title}</span>
            </div>
            <p style={{ margin: 0, fontSize: 12, color: "#64748b", lineHeight: 1.45, flex: 1 }}>{body}</p>
            {action}
        </div>
    );

    return (
        <div style={{
            background: "linear-gradient(135deg, #eff6ff 0%, #f8fafc 100%)",
            border: "1px solid #bfdbfe", borderRadius: 12, padding: 18, marginBottom: 20,
        }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 14 }}>
                <div>
                    <h3 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: "#1a3a5c" }}>Easy annual update</h3>
                    <p style={{ margin: "4px 0 0", fontSize: 12, color: "#475569", maxWidth: 720, lineHeight: 1.45 }}>
                        One SuperAdmin update for the whole platform. Copy last year, apply SSA/SDI defaults, then replace FITW from Pub 15-T via Excel CSV — no hand-editing JSON.
                    </p>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <label style={{ fontSize: 12, fontWeight: 600 }}>From</label>
                    <input type="number" value={fromYear} onChange={e => setFromYear(Number(e.target.value))} style={{ ...inp, width: 72, padding: "6px 8px" }} />
                    <span>→</span>
                    <label style={{ fontSize: 12, fontWeight: 600 }}>Target</label>
                    <input type="number" value={toYear} onChange={e => setToYear(Number(e.target.value))} style={{ ...inp, width: 72, padding: "6px 8px" }} />
                    {status && (
                        <span style={{ fontSize: 12, fontWeight: 700, color: "#334155" }}>
                            {Object.values(status.checklist || {}).filter(i => i?.ready).length}/5 ready
                        </span>
                    )}
                </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
                {stepBox(
                    1,
                    "Copy prior year",
                    `Copies brackets, tax settings, SDI, and local rates from ${fromYear} into ${toYear} (overwrite). Gives you a full pack to edit.`,
                    <button type="button" onClick={() => setConfirmCopy(true)} disabled={copyMut.isPending}
                        style={{ ...btn, background: "#1B75F0", color: "#fff", padding: "8px 12px", alignSelf: "flex-start" }}>
                        {copyMut.isPending ? "Copying…" : `Copy ${fromYear} → ${toYear}`}
                    </button>
                )}
                {stepBox(
                    2,
                    "Apply published defaults",
                    `Sets curated SSA wage base and high-confidence SDI (e.g. CA 1.3%, NJ TDI+FLI) for ${toYear} when we have a pack. Other SDI states still need a quick check.`,
                    <button type="button" onClick={() => setConfirmDefaults(true)} disabled={defaultsMut.isPending}
                        style={{ ...btn, background: "#0f766e", color: "#fff", padding: "8px 12px", alignSelf: "flex-start" }}>
                        {defaultsMut.isPending ? "Applying…" : `Apply defaults for ${toYear}`}
                    </button>
                )}
                {stepBox(
                    3,
                    "Update FITW in Excel",
                    "Export last year’s FITW CSV, edit wage thresholds/rates from IRS Pub 15-T, then upload on the Federal tab (dry-run → import).",
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        <button type="button"
                            onClick={() => yearApi.downloadCsv(
                                "/admin/tax-year/export-brackets",
                                `fitw-US-FITW-${fromYear}.csv`,
                                { year: fromYear, jurisdiction: "US", taxType: "FITW" }
                            ).catch(e => setStepMsg(e?.response?.data?.message || "Export failed."))
                            }
                            style={{ ...btn, background: "#fff", border: "1px solid #1B75F0", color: "#1B75F0", padding: "8px 12px" }}>
                            Export {fromYear} FITW CSV
                        </button>
                        <button type="button"
                            onClick={() => yearApi.downloadCsv(
                                "/admin/tax-year/fitw-template",
                                "fitw-import-template.csv"
                            ).catch(e => setStepMsg(e?.response?.data?.message || "Template download failed."))
                            }
                            style={{ ...btn, background: "#fff", border: "1px solid #94a3b8", color: "#475569", padding: "8px 12px" }}>
                            Blank template
                        </button>
                    </div>
                )}
            </div>

            {stepMsg && (
                <p style={{
                    margin: "12px 0 0", fontSize: 12, lineHeight: 1.45,
                    color: /fail|error/i.test(stepMsg) ? "#dc2626" : "#15803d",
                }}>{stepMsg}</p>
            )}

            <ConfirmModal
                show={confirmCopy}
                title="Copy full tax year pack?"
                message={`Overwrite ${toYear} with brackets, settings, SDI, and local rates from ${fromYear}?`}
                confirmText="Overwrite & Copy"
                onConfirm={() => copyMut.mutate()}
                onCancel={() => setConfirmCopy(false)}
            />
            <ConfirmModal
                show={confirmDefaults}
                title="Apply published defaults?"
                message={`Overwrite SS_WAGE_BASE and curated SDI rows for ${toYear} with platform-published figures?`}
                confirmText="Apply defaults"
                onConfirm={() => defaultsMut.mutate()}
                onCancel={() => setConfirmDefaults(false)}
            />
        </div>
    );
};

const YearRolloverChecklist = () => {
    const qc = useQueryClient();
    const calendarYear = new Date().getFullYear();
    const [targetYear, setTargetYear] = useState(calendarYear);
    const [fromYear, setFromYear] = useState(calendarYear - 1);
    const [overwrite, setOverwrite] = useState(false);
    const [confirmCopy, setConfirmCopy] = useState(false);
    const [msg, setMsg] = useState("");
    const [opts, setOpts] = useState({
        copyBrackets: true,
        copySettings: true,
        copyFlatRates: true,
        copyLocalRates: true,
    });

    const { data: status, isLoading } = useQuery({
        queryKey: ["tax-year-status", targetYear],
        queryFn: () => yearApi.status(targetYear),
    });

    const copyMut = useMutation({
        mutationFn: () => yearApi.copy({
            fromYear: Number(fromYear),
            toYear: Number(targetYear),
            overwrite,
            ...opts,
        }),
        onSuccess: (r) => {
            const parts = Object.entries(r.copied || {}).map(([k, v]) => `${k}: ${v}`).join(", ");
            setMsg(`Copied ${fromYear} → ${targetYear} (${parts || "nothing"}). Review FITW against Pub 15-T.`);
            setConfirmCopy(false);
            qc.invalidateQueries({ queryKey: ["tax-year-status"] });
            qc.invalidateQueries({ queryKey: ["tb-years"] });
            qc.invalidateQueries({ queryKey: ["tb"] });
            qc.invalidateQueries({ queryKey: ["tax-settings"] });
            qc.invalidateQueries({ queryKey: ["sdi-rates"] });
        },
        onError: (e) => {
            setMsg(e?.response?.data?.message || "Full-year copy failed.");
            setConfirmCopy(false);
        },
    });

    const items = status?.checklist
        ? [
            status.checklist.fitw,
            status.checklist.sit,
            status.checklist.sdi,
            status.checklist.local,
            status.checklist.settings,
        ]
        : [];

    const readyCount = items.filter(i => i?.ready).length;

    return (
        <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 18, marginBottom: 24 }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
                <div>
                    <h3 style={{ margin: 0, fontSize: 14, fontWeight: 800, color: "#1a3a5c" }}>Coverage checklist (advanced)</h3>
                    <p style={{ margin: "4px 0 0", fontSize: 12, color: "#64748b", maxWidth: 640, lineHeight: 1.45 }}>
                        {status?.payrollRule || "Payroll uses pay-date year first, then latest available."}
                        {status?.missingCalendarYear && (
                            <span style={{ display: "block", marginTop: 4, color: "#dc2626", fontWeight: 600 }}>
                                Calendar year {calendarYear} has no bracket pack yet.
                            </span>
                        )}
                    </p>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <label style={{ fontSize: 12, fontWeight: 600 }}>Check year:</label>
                    <input type="number" value={targetYear} onChange={e => setTargetYear(Number(e.target.value))}
                        style={{ ...inp, width: 80, padding: "6px 8px" }} />
                    <span style={{ fontSize: 12, fontWeight: 700, color: readyCount === 5 ? "#16a34a" : "#b45309" }}>
                        {isLoading ? "…" : `${readyCount}/5 ready`}
                    </span>
                </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 8, marginBottom: 14 }}>
                {items.map(item => (
                    <div key={item.label} style={{
                        border: `1px solid ${item.ready ? "#86efac" : "#fde68a"}`,
                        background: item.ready ? "#f0fdf4" : "#fffbeb",
                        borderRadius: 8, padding: "10px 12px",
                    }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: item.ready ? "#15803d" : "#a16207" }}>
                            {item.ready ? "✓" : "○"} {item.label}
                        </div>
                        <div style={{ fontSize: 11, color: "#64748b", marginTop: 4 }}>{item.count} row(s)</div>
                    </div>
                ))}
            </div>

            <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: 12 }}>
                <p style={{ margin: "0 0 8px", fontSize: 12, fontWeight: 700, color: "#334155" }}>Copy full year pack</p>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginBottom: 8 }}>
                    <div>
                        <label style={{ fontSize: 11, fontWeight: 600, marginRight: 4 }}>From</label>
                        <input type="number" value={fromYear} onChange={e => setFromYear(Number(e.target.value))} style={{ ...inp, width: 72 }} />
                    </div>
                    <span>→</span>
                    <div>
                        <label style={{ fontSize: 11, fontWeight: 600, marginRight: 4 }}>To</label>
                        <input type="number" value={targetYear} onChange={e => setTargetYear(Number(e.target.value))} style={{ ...inp, width: 72 }} />
                    </div>
                    {[
                        ["copyBrackets", "Brackets"],
                        ["copySettings", "Settings"],
                        ["copyFlatRates", "SDI"],
                        ["copyLocalRates", "Local"],
                    ].map(([key, label]) => (
                        <label key={key} style={{ fontSize: 11, display: "flex", gap: 4, alignItems: "center", cursor: "pointer" }}>
                            <input type="checkbox" checked={!!opts[key]} onChange={e => setOpts(o => ({ ...o, [key]: e.target.checked }))} />
                            {label}
                        </label>
                    ))}
                    <label style={{ fontSize: 11, display: "flex", gap: 4, alignItems: "center", cursor: "pointer" }}>
                        <input type="checkbox" checked={overwrite} onChange={e => setOverwrite(e.target.checked)} />
                        Overwrite
                    </label>
                    <button type="button" onClick={() => setConfirmCopy(true)} disabled={copyMut.isPending}
                        style={{ ...btn, background: "#1B75F0", color: "#fff", padding: "6px 14px" }}>
                        {copyMut.isPending ? "Copying…" : "Copy year pack"}
                    </button>
                </div>
                {msg && <p style={{ margin: 0, fontSize: 12, color: String(msg).includes("Copied") ? "#16a34a" : "#dc2626" }}>{msg}</p>}
            </div>

            <ConfirmModal
                show={confirmCopy}
                title="Copy full tax year pack?"
                message={`${overwrite ? "Overwrite existing data and copy" : "Copy"} brackets/settings/SDI/local from ${fromYear} → ${targetYear}. Then update FITW from IRS Pub 15-T.`}
                confirmText={overwrite ? "Overwrite & Copy" : "Copy"}
                onConfirm={() => copyMut.mutate()}
                onCancel={() => setConfirmCopy(false)}
            />
        </div>
    );
};

const FitwImportPanel = () => {
    const qc = useQueryClient();
    const [year, setYear] = useState(new Date().getFullYear());
    const [text, setText] = useState("");
    const [overwrite, setOverwrite] = useState(true);
    const [preview, setPreview] = useState(null);
    const [msg, setMsg] = useState("");
    const [confirmImport, setConfirmImport] = useState(false);

    const parsePayload = () => {
        const raw = text.trim();
        if (!raw) throw new Error("Paste or upload JSON/CSV first.");
        if (raw.startsWith("{") || raw.startsWith("[")) {
            const doc = JSON.parse(raw);
            const brackets = Array.isArray(doc) ? doc : (doc.brackets || []);
            const y = Array.isArray(doc) ? year : (doc.year || year);
            return {
                year: Number(y),
                jurisdiction: doc.jurisdiction || "US",
                taxType: doc.taxType || "FITW",
                brackets: brackets.map(b => ({
                    filingStatus: b.filingStatus || b.FilingStatus,
                    minWage: b.minWage ?? b.MinWage ?? null,
                    maxWage: b.maxWage ?? b.MaxWage ?? null,
                    baseAmount: b.baseAmount ?? b.BaseAmount ?? 0,
                    rate: Number(b.rate ?? b.Rate ?? 0),
                    excessOver: b.excessOver ?? b.ExcessOver ?? 0,
                })),
            };
        }
        // CSV: filingStatus,minWage,maxWage,baseAmount,rate,excessOver
        const lines = raw.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
        const start = lines[0].toLowerCase().includes("filing") ? 1 : 0;
        const brackets = [];
        for (let i = start; i < lines.length; i++) {
            const cols = lines[i].split(",").map(c => c.trim());
            if (cols.length < 5) continue;
            brackets.push({
                filingStatus: cols[0],
                minWage: cols[1] === "" ? null : Number(cols[1]),
                maxWage: cols[2] === "" || cols[2] === "null" ? null : Number(cols[2]),
                baseAmount: Number(cols[3] || 0),
                rate: Number(cols[4] || 0),
                excessOver: Number(cols[5] || 0),
            });
        }
        return { year: Number(year), jurisdiction: "US", taxType: "FITW", brackets };
    };

    const onFile = (file) => {
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => {
            setText(String(reader.result || ""));
            setPreview(null);
            setMsg(`Loaded ${file.name} — click Dry-run preview.`);
        };
        reader.onerror = () => setMsg("Could not read file.");
        reader.readAsText(file);
    };

    const dryRunMut = useMutation({
        mutationFn: () => {
            const payload = parsePayload();
            return yearApi.importBrackets({ ...payload, dryRun: true, overwrite });
        },
        onSuccess: (r) => { setPreview(r); setMsg(r.message || "Dry-run OK"); },
        onError: (e) => {
            setPreview(null);
            const err = e?.response?.data;
            setMsg(err?.message || e.message || "Dry-run failed");
            if (err?.errors) setMsg((err.message || "Validation failed") + ": " + err.errors.join("; "));
        },
    });

    const importMut = useMutation({
        mutationFn: () => {
            const payload = parsePayload();
            return yearApi.importBrackets({ ...payload, dryRun: false, overwrite });
        },
        onSuccess: (r) => {
            setMsg(r.message || "Imported");
            setConfirmImport(false);
            setPreview(null);
            qc.invalidateQueries({ queryKey: ["tb"] });
            qc.invalidateQueries({ queryKey: ["tb-years"] });
            qc.invalidateQueries({ queryKey: ["tax-year-status"] });
        },
        onError: (e) => {
            setConfirmImport(false);
            setMsg(e?.response?.data?.message || "Import failed");
        },
    });

    return (
        <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: 16, marginBottom: 20 }}>
            <p style={{ fontWeight: 700, fontSize: 13, margin: "0 0 4px", color: "#1a3a5c" }}>Import FITW (Excel CSV or JSON)</p>
            <p style={{ fontSize: 11, color: "#64748b", margin: "0 0 10px", lineHeight: 1.45 }}>
                Best path: export prior-year CSV from Easy annual update → edit in Excel from Pub 15-T → upload here.
                Rate as decimal (0.12 = 12%). Dry-run first, then import with overwrite.
            </p>
            <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 8, flexWrap: "wrap" }}>
                <label style={{ fontSize: 12, fontWeight: 600 }}>Year (CSV)</label>
                <input type="number" value={year} onChange={e => setYear(Number(e.target.value))} style={{ ...inp, width: 80 }} />
                <label style={{ fontSize: 12, display: "flex", gap: 6, alignItems: "center" }}>
                    <input type="checkbox" checked={overwrite} onChange={e => setOverwrite(e.target.checked)} />
                    Overwrite existing US/FITW for year
                </label>
                <label style={{ ...btn, background: "#fff", border: "1px solid #1B75F0", color: "#1B75F0", padding: "6px 12px", display: "inline-flex", alignItems: "center" }}>
                    Upload CSV/JSON
                    <input type="file" accept=".csv,.json,.txt,text/csv,application/json" style={{ display: "none" }}
                        onChange={e => { onFile(e.target.files?.[0]); e.target.value = ""; }} />
                </label>
                <button type="button"
                    onClick={() => yearApi.downloadCsv("/admin/tax-year/fitw-template", "fitw-import-template.csv")
                        .catch(() => setMsg("Template download failed"))}
                    style={{ ...btn, background: "#fff", border: "1px solid #94a3b8", color: "#475569", padding: "6px 12px" }}>
                    Download template
                </button>
                <button type="button"
                    onClick={() => yearApi.downloadCsv(
                        "/admin/tax-year/export-brackets",
                        `fitw-US-FITW-${year}.csv`,
                        { year, jurisdiction: "US", taxType: "FITW" }
                    ).catch(() => setMsg("Export failed"))}
                    style={{ ...btn, background: "#fff", border: "1px solid #94a3b8", color: "#475569", padding: "6px 12px" }}>
                    Export this year
                </button>
            </div>
            <textarea
                value={text}
                onChange={e => setText(e.target.value)}
                rows={7}
                placeholder={"Paste CSV or JSON, or use Upload CSV/JSON.\nfilingStatus,minWage,maxWage,baseAmount,rate,excessOver\nS,0,6000,0,0,0"}
                style={{ width: "100%", fontFamily: "monospace", fontSize: 11, border: "1px solid #d1d5db", borderRadius: 6, padding: 10, boxSizing: "border-box", marginBottom: 8 }}
            />
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                <button type="button" onClick={() => dryRunMut.mutate()} disabled={dryRunMut.isPending}
                    style={{ ...btn, background: "#fff", border: "1px solid #1B75F0", color: "#1B75F0", padding: "6px 14px" }}>
                    {dryRunMut.isPending ? "Checking…" : "Dry-run preview"}
                </button>
                <button type="button" onClick={() => setConfirmImport(true)} disabled={!preview || importMut.isPending}
                    style={{ ...btn, background: preview ? "#16a34a" : "#94a3b8", color: "#fff", padding: "6px 14px", cursor: preview ? "pointer" : "not-allowed" }}>
                    Import
                </button>
                {msg && <span style={{ fontSize: 12, color: String(msg).toLowerCase().includes("fail") || String(msg).toLowerCase().includes("valid") ? "#dc2626" : "#15803d" }}>{msg}</span>}
            </div>
            {preview?.preview && (
                <p style={{ fontSize: 11, color: "#64748b", margin: "8px 0 0" }}>
                    Would import <strong>{preview.wouldImport}</strong> rows for {preview.jurisdiction}/{preview.taxType} {preview.year}
                    {preview.existingCount ? ` (existing: ${preview.existingCount})` : ""}. Showing first {preview.preview.length} in API response.
                </p>
            )}
            <ConfirmModal
                show={confirmImport}
                title="Import FITW brackets?"
                message={overwrite
                    ? `Replace existing US/FITW rows for the import year, then insert ${preview?.wouldImport || "?"} brackets.`
                    : `Insert ${preview?.wouldImport || "?"} brackets. Will fail if the year already has US/FITW rows.`}
                confirmText="Import"
                onConfirm={() => importMut.mutate()}
                onCancel={() => setConfirmImport(false)}
            />
        </div>
    );
};

const TaxBracketAdmin = () => {
    const [tab, setTab] = useState("federal");

    const tabStyle = active => ({
        padding: "12px 32px",
        fontSize: 15,
        fontWeight: active ? 700 : 500,
        border: "none",
        borderBottom: active ? "3px solid #1B75F0" : "3px solid transparent",
        background: "transparent",
        color: active ? "#1B75F0" : "#64748b",
        cursor: "pointer",
        transition: "all 0.15s",
        letterSpacing: 0.2,
    });

    return (
        <div style={{ padding: "24px", maxWidth: 1100 }}>
            <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 4 }}>Tax Bracket Administration</h2>
            <p style={{ fontSize: 13, color: "#666", marginBottom: 20 }}>
                Platform tax tables — update once for all companies. Use Easy annual update each fall when IRS/SSA publish figures.
            </p>

            <EasyAnnualUpdate />
            <YearRolloverChecklist />

            {/* Tabs */}
            <div style={{ display: "flex", borderBottom: "1px solid #e2e8f0", marginBottom: 24, flexWrap: "wrap" }}>
                <button style={tabStyle(tab === "federal")} onClick={() => setTab("federal")}>
                    US Federal (FITW)
                </button>
                <button style={tabStyle(tab === "state")} onClick={() => setTab("state")}>
                    State (SIT)
                </button>
                <button style={tabStyle(tab === "sdi-local")} onClick={() => setTab("sdi-local")}>
                    SDI &amp; Local Rates
                </button>
                <button style={tabStyle(tab === "tax-settings")} onClick={() => setTab("tax-settings")}>
                    Tax Settings
                </button>
                <button style={tabStyle(tab === "reciprocity")} onClick={() => setTab("reciprocity")}>
                    State Reciprocity
                </button>
            </div>

            {tab === "federal" && (
                <div>
                    <div style={{ background: "#fef3c7", border: "1px solid #fcd34d", borderRadius: 6, padding: "10px 14px", marginBottom: 20, fontSize: 12, color: "#92400e" }}>
                        <strong>Federal Income Tax Withholding (IRS Pub 15-T)</strong> — These brackets are used for all employees based on their W-4 filing status. Stored as jurisdiction <code>US</code>, type <code>FITW</code>.
                    </div>
                    <FitwImportPanel />
                    <BracketTable jurisdiction="US" taxType="FITW" />
                </div>
            )}

            {tab === "state" && (
                <div>
                    <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 6, padding: "10px 14px", marginBottom: 20, fontSize: 12, color: "#166534" }}>
                        <strong>State Income Tax (SIT)</strong> — Select a state to view and manage its progressive tax brackets. Stored as jurisdiction = state code, type <code>SIT</code>.
                    </div>
                    <StateBracketTable />
                </div>
            )}

            {tab === "sdi-local" && (
                <div>
                    <div style={{ background: "#f5f3ff", border: "1px solid #ddd6fe", borderRadius: 6, padding: "10px 14px", marginBottom: 24, fontSize: 12, color: "#5b21b6" }}>
                        <strong>SDI &amp; Local Tax Rates</strong> — Update these annually. SDI rates change most years in CA, NJ, WA. Local rates rarely change but verify each January. Use Year Rollover checklist to copy prior year.
                    </div>

                    <div style={{ marginBottom: 32 }}>
                        <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 4, color: "#1a3a5c" }}>State Disability Insurance (SDI) / Paid Leave Rates</h3>
                        <p style={{ fontSize: 12, color: "#6b7280", marginBottom: 12 }}>
                            Employee-side SDI/PFML rates by state and year. Applies to: CA, CO, CT, HI, MA, NJ, NY, OR, RI, WA.
                        </p>
                        <SdiRatesTable />
                    </div>

                    <hr style={{ borderColor: "#e5e7eb", marginBottom: 28 }} />

                    <div>
                        <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 4, color: "#1a3a5c" }}>Local / City / County Tax Rates</h3>
                        <p style={{ fontSize: 12, color: "#6b7280", marginBottom: 12 }}>
                            Local income tax rates by state and locality code. Applies to: IN (counties), KY, MD, MI, MO, NY, OH, PA.
                        </p>
                        <LocalTaxRatesTable />
                    </div>
                </div>
            )}

            {tab === "tax-settings" && <TaxSettingsTab />}

            {tab === "reciprocity" && (
                <div>
                    <div style={{ background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 6, padding: "10px 14px", marginBottom: 20, fontSize: 12, color: "#0369a1" }}>
                        <strong>State Reciprocity Agreements</strong> — When a reciprocity agreement exists between an employee's resident state and work state,
                        they only pay income tax to their <em>resident</em> state. Both directions of each pair are stored (e.g. NJ→PA and PA→NJ).
                        Toggle <strong>Active</strong> to temporarily suspend an agreement without deleting it. Source: each state's Department of Revenue.
                    </div>
                    <ReciprocityTable />
                </div>
            )}
        </div>
    );
};

const TaxSettingsTab = () => {
    const [settingsYear, setSettingsYear] = useState(new Date().getFullYear());
    const years = [2024, 2025, 2026, 2027, 2028];
    return (
        <div>
            <div style={{ background: "#EFF6FF", border: "1px solid #fed7aa", borderRadius: 6, padding: "10px 14px", marginBottom: 20, fontSize: 12, color: "#9a3412" }}>
                <strong>Annual Tax Settings</strong> — Update SS wage base each November. Update SUTA wage bases when states publish new limits. FUTA credit reduction states are published by DOL in November.
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
                <label style={{ fontSize: 12, fontWeight: 600 }}>Tax Year:</label>
                <select value={settingsYear} onChange={e => setSettingsYear(Number(e.target.value))} style={{ ...sel, padding: "6px 28px 6px 10px" }}>
                    {years.map(y => <option key={y}>{y}</option>)}
                </select>
            </div>
            {SETTING_KEYS.map(sk => (
                <TaxSettingsTable key={sk.key} settingKey={sk.key} label={sk.label} hasState={sk.hasState} format={sk.format} hint={sk.hint} year={settingsYear} />
            ))}
        </div>
    );
};

export default TaxBracketAdmin;
